import "./AddCandidatesDialog.css";
import "../JobDetailForm/JobDetailForm.css";
 
import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import axios from "axios";
import { useCookies } from "react-cookie";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
 
const MAX_RESUME_UPLOADS = 50;
 
const ResumeUploadDialog = ({
    job_id,
    closeModal = null,
    refreshData = null,
}) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [resumeText, setResumeText] = useState("");
    const [selectedTab, setSelectedTab] = useState(0);
    const [cookies] = useCookies(["AuthorizationToken"]);
    const [errorMessage, setErrorMessage] = useState('');
    const [showError, setShowError] = useState(false);
 
    const handleFileChange = (event) => {
        let files = Array.from(event.target.files);
 
        if (event.target.files.length > MAX_RESUME_UPLOADS) {
            files = Array.from(event.target.files).slice(0, MAX_RESUME_UPLOADS);
            alert(
                `A maximum of ${MAX_RESUME_UPLOADS} resumes can be uploaded at once. Your selection has been reduced to the first ${MAX_RESUME_UPLOADS} resumes. Click Upload to accept this selection or click "Choose Files" again and limit to ${MAX_RESUME_UPLOADS} resumes.`
            );
        }
 
        console.log("Selected files:", files.length);
        setSelectedFiles(files);
    };
 
    const handleUploadSubmit = async (event) => {
        event.preventDefault();
        setIsUploading(true);
 
        const formData = new FormData();
        selectedFiles.forEach((file) => {
            formData.append("resume", file);
        });
 
        try {
            const response = await axios.post(
                `https://api.foveaai.com/evaluate/web/candidates/upload/${job_id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: cookies.AuthorizationToken,
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(percentCompleted);
                    },
                }
            );
            if (response.status !== 202) {
                throw new Error(response.data.message);
            }
 
            // Successfully uploaded so clean up and close modal
            setResumeText("");
            if (refreshData) refreshData();
            if (closeModal) closeModal();
        } catch (error) {
            console.error("Upload Error Details:", {
                message: error.message,
                response: error.response,
                responseData: error.response?.data,
                responseStatus: error.response?.status,
                responseHeaders: error.response?.headers,
                request: error.request,
                config: error.config,
                stack: error.stack
            });
            setErrorMessage(error.response?.data?.message || "An unexpected error occurred.");
            setShowError(true);
        } finally {
            // Reset upload state since we were either successful or an error occurred
            setIsUploading(false);
            setUploadProgress(0);
        }
    };
 
    const handleTextSubmit = async (event) => {
        event.preventDefault();
        setIsUploading(true);
    
        try {
            const response = await axios.post(
                `https://api.foveaai.com/evaluate/web/candidates/job/${job_id}`,
                {
                    resume: resumeText,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: cookies.AuthorizationToken,
                    },
                }
            );
            if (response.status !== 202) {
                throw new Error(response.data.message);
            }
            console.log("Submitted resume text:", response.data);
 
            // Successfully uploaded so clean up and close modal
            setResumeText("");
            if (refreshData) refreshData();
            if (closeModal) closeModal();
        } catch (error) {
            // Error uploading so display error message
            console.error("Text Upload Error Details:", {
                message: error.message,
                response: error.response,
                responseData: error.response?.data,
                responseStatus: error.response?.status,
                responseHeaders: error.response?.headers,
                request: error.request,
                config: error.config,
                stack: error.stack
            });
            setErrorMessage(error.response?.data?.message || "An unexpected error occurred.");
            setShowError(true);
        } finally {
            // Reset upload state since we were either successful or an error occurred
            setIsUploading(false);
        }
    };
 
    const uploadFormRef = React.useRef(null);
    const textFormRef = React.useRef(null);
 
    const handlePrimaryAction = () => {
        if (selectedTab === 0 && uploadFormRef.current) {
            if (typeof uploadFormRef.current.requestSubmit === 'function') uploadFormRef.current.requestSubmit();
            else uploadFormRef.current.submit();
        } else if (selectedTab === 1 && textFormRef.current) {
            if (typeof textFormRef.current.requestSubmit === 'function') textFormRef.current.requestSubmit();
            else textFormRef.current.submit();
        }
    };
 
    return (
        <div className="job-modal">
            <div className="job-modal-header">
                <div className="header-left">
                    <div className="modal-title job-modal-title">Add Candidates</div>
                </div>
                {closeModal && (
                    <button className="close-btn" aria-label="Close" onClick={closeModal}>×</button>
                )}
            </div>
            <div className="job-modal-body">
                <div className="job-main-content">
                    <Tabs selectedIndex={selectedTab} onSelect={(i) => setSelectedTab(i)}>
                        <TabList className="job-tabs">
                            <Tab className="job-tab">Upload Resumes</Tab>
                            <Tab className="job-tab">Paste Resume</Tab>
                        </TabList>

                        <TabPanel>
                            <div className="tab-content">
                                <Form ref={uploadFormRef} onSubmit={handleUploadSubmit}>
                                    <div className="quick-tips">
                                        <div className="tips-icon">📤</div>
                                        <div className="tips-content">
                                            <div className="tips-title">Upload resumes</div>
                                            <div className="tips-text">Upload PDF, Microsoft Word, or plain text resumes. Evaluations take approximately 2 minutes per resume uploaded and appear after a page refresh.</div>
                                        </div>
                                    </div>
                                    <Form.Group className="mb-3">
                                        <Form.Control
                                            type="file"
                                            name="file"
                                            id="resumeUpload"
                                            multiple
                                            onChange={handleFileChange}
                                        />
                                    </Form.Group>
                                    <div
                                        className="progress"
                                        style={{ visibility: isUploading ? "visible" : "hidden", minHeight: "25px" }}
                                    >
                                        {isUploading && (
                                            <div
                                                className="progress-bar"
                                                role="progressbar"
                                                style={{ width: `${uploadProgress}%` }}
                                                aria-valuenow={uploadProgress}
                                                aria-valuemin="0"
                                                aria-valuemax="100"
                                            >
                                                {uploadProgress}%
                                            </div>
                                        )}
                                    </div>
                                </Form>
                            </div>
                        </TabPanel>

                        <TabPanel>
                            <div className="tab-content">
                                <Form ref={textFormRef} onSubmit={handleTextSubmit}>
                                    <div className="quick-tips">
                                        <div className="tips-icon">📋</div>
                                        <div className="tips-content">
                                            <div className="tips-title">Paste Resume</div>
                                            <div className="tips-text">Copy and paste the resume text below. Evaluation takes approximately 2 minutes and appears after a refresh.</div>
                                        </div>
                                    </div>
                                    <Form.Group className="mb-3">
                                        <Form.Control
                                            as="textarea"
                                            name="resumeText"
                                            id="resumeText"
                                            value={resumeText}
                                            rows={8}
                                            onChange={(e) => setResumeText(e.target.value)}
                                            placeholder="Copy and paste resume text here..."
                                        />
                                    </Form.Group>
                                </Form>
                            </div>
                        </TabPanel>
                     </Tabs>

                    {showError && (
                        <div className="error-confirmation">
                            <h2>Upload Error</h2>
                            <p>{errorMessage}</p>
                            <Button type="button" variant="secondary" onClick={() => setShowError(false)}>Dismiss</Button>
                        </div>
                    )}
                </div>
            </div>

            <div className="job-modal-footer">
                {closeModal && (
                    <Button type="button" className="btn-modern-secondary" onClick={closeModal} disabled={isUploading}>
                        Cancel
                    </Button>
                )}
                <Button type="button" className="btn-modern-primary" onClick={handlePrimaryAction} disabled={isUploading}>
                    {selectedTab === 0 ? 'Upload' : 'Submit'}
                </Button>
            </div>
         </div>
     );
 };
 
 export default ResumeUploadDialog;
